import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom'; // 👈 Added useLocation
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import EditIcon from '@mui/icons-material/Edit';
import Card from '@mui/material/Card';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import PlatformImage from '../../General/PlatformImage'
import PlatformDrawer from '../../General/PlatformDrawer';
import SettingsStatus from '../../../pages/General/SettingsStatus'
import { fetchchannelconfig } from '../../../pages/Integration/Notification/PlatformIntegrations/Email/emailSlice';

const IntegratedPage = ({ loading, data }) => {
    const location = useLocation(); // 👈 Access current route location/query
    const channelconfig = useSelector(state => state.email && state.email.channelconfig);
    const [currentCategory, setCurrentCategory] = useState('all');
    const [orderDrawerVisible, setOrderDrawerVisible] = useState(false);
    const [platformName, setPlatformName] = useState('all');
    const [storeName, setStoreName] = useState('all');
    const [storeId, setStoreId] = useState('all');

    const dispatch = useDispatch();

    const platformCallback = (platform) => {
        setPlatformName(platform)
        setOrderDrawerVisible(true)
    }

    const orderOnClose = (values) => {
        setOrderDrawerVisible(false);
    };

    // ─── READ QUERY PARAMS TO RE-OPEN DRAWER ON MOUNT / RETURN ───
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const drawerParam = queryParams.get('drawer') || queryParams.get('platform');

        if (drawerParam) {
            setPlatformName(drawerParam.toUpperCase());
            setOrderDrawerVisible(true);
        }
    }, [location.search]);

    const columns = [
        {
            field: "notification_type",
            headerName: 'Platform',
            width: 100,
            resizable: false,
            flex: 1,
            renderCell: (params) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <PlatformImage platform={params.row.notification_type} width={"35px"} />
                    </Box>
                )
            }
        },
        {
            field: 'is_enabled',
            headerName: 'Status',
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <SettingsStatus settingsStatus={params.row.is_enabled} />
                    </Box>
                )
            }
        },
        {
            field: 'Actions',
            headerName: 'Actions',
            resizable: false,
            flex: 1,
            renderCell: (params) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Tooltip title="Configure Platform" placement="top">
                            <IconButton
                                onClick={(e) => {
                                    platformCallback(params.row.notification_type)
                                }}
                                sx={{ 
                                    color: '#1976d2',
                                    padding: '6px',
                                    borderRadius: '8px',
                                    border: '1px solid #E2E8F0',
                                    backgroundColor: '#FFFFFF',
                                    '&:hover': { backgroundColor: '#F1F5F9', borderColor: '#CBD5E1' }
                                }}
                            >
                                <EditIcon sx={{ fontSize: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        }
    ];

    useEffect(() => {
        dispatch(fetchchannelconfig());
    }, [dispatch])

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
            {loading && <Card loading={loading} />}
            
            {/* Component Layout Operational Header Toolbar */}
            <Box 
                className="integrated--stores--title" 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                    width: '100%'
                }}
            >
                <Typography 
                    variant="h5" 
                    component="h2" 
                    sx={{ 
                        fontWeight: 800, 
                        color: '#0F172A', 
                        letterSpacing: '-0.5px',
                        fontSize: '1.25rem',
                        m: 0
                    }}
                >
                    Connected Notification Platforms
                </Typography>
                
                <Box sx={{ m: 0 }}>
                    <Link to="/reports/notifications" style={{ textDecoration: 'none' }}>
                        <Button
                            variant="outlined"
                            sx={{
                                textTransform: 'none',
                                borderRadius: '10px',
                                fontWeight: 700,
                                fontSize: '13px',
                                height: '36px',
                                px: 2.5,
                                borderColor: '#E2E8F0',
                                color: '#475569',
                                '&:hover': {
                                    borderColor: '#1976d2',
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    color: '#1976d2'
                                }
                            }}
                        >
                            Notification Logs
                        </Button>
                    </Link>
                </Box>
            </Box>

            {/* Main Interactive Matrix Dashboard Table Grid Wrapper */}
            {channelconfig && channelconfig.loading === false && (
                <Box className="integrated--stores--list" sx={{ width: '100%', backgroundColor: '#FFFFFF' }}>
                    <Box sx={{ width: '100%' }}>
                        <DataGrid
                            sx={{
                                border: 'none',
                                backgroundColor: '#FFFFFF',
                                overflow: 'hidden',
                                '& .MuiDataGrid-topContainer': { backgroundColor: '#F8FAFC', '&::after': { display: 'none' } },
                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', minHeight: '52px !important', maxHeight: '52px !important' },
                                '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px', '&:focus, &:focus-within': { outline: 'none' } },
                                '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease-in-out', borderBottom: '1px solid #F1F5F9', alignItems: 'center', '&:hover': { backgroundColor: '#F8FAFC' } },
                                '& .MuiDataGrid-cell': { fontSize: '13.5px', color: '#334155', borderBottom: 'none', display: 'flex', alignItems: 'center',  '&:focus, &:focus-within': { outline: 'none' } },
                                '& .MuiLinearProgress-root': { height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }
                            }}
                            getRowId={(row) => row._id}
                            rows={channelconfig && channelconfig.data}
                            columns={columns}
                            autoHeight={true}
                            disableRowSelectionOnClick
                            disableColumnFilter={true}
                            disableColumnMenu={true}
                            hideFooter={true}
                            slots={{
                                loadingOverlay: LinearProgress,
                            }}
                            loading={
                                (channelconfig && channelconfig.loading)
                            }
                        />
                    </Box>
                </Box>
            )}

            <PlatformDrawer
                visible={orderDrawerVisible}
                onClose={orderOnClose}
                platform={platformName}
            />
        </Box>
    )
}

export default IntegratedPage;